import styled from "styled-components";
import { NavLink } from "react-router-dom";
import { rgba, darken } from "polished";

import { Chip } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useState } from "react";

const Item = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  padding: 5px 0;

  padding-left: ${(props) => (props.depth ? props.depth * 56 : 28)}px;
  padding-right: 16px;
  font-weight: ${(props) => props.theme.typography.fontWeightRegular};

  svg {
    color: ${(props) => props.theme.sidebar.color};
    font-size: 20px;
    width: 18px;
    height: 18px;
    opacity: 0.65;
  }

  &:hover {
    background: ${(props) => darken(0.05, props.theme.sidebar.active)};
    color: ${(props) => props.theme.sidebar.color};
  }

  &.${(props) => props.activeclassname} {
    background-color: ${(props) => props.theme.sidebar.active};
    span {
      color: ${(props) => props.theme.sidebar.color};
    }
  }
`;

const ItemHighlight = styled.div`
  position: absolute;
  height: 100%;
  top: 0;
  left: 0;
  bottom: 0;
  width: 6px;
  background-color: ${(props) => props.theme.palette.primary.main};
  opacity: 0;
  transition: opacity linear 0.1s;
  border-radius: 0 2px 2px 0;
`;

const Title = styled.div`
  margin: 0;
  color: ${(props) => (props.depth > 0 ? "#ccc" : "#eee")};
  font-weight: ${(props) => (props.depth > 0 ? "normal" : "600")};
  font-size: ${(props) => props.theme.typography.body1.fontSize}px;
  padding: 2.5px 10px;
  user-select: none;
`;

const Badge = styled(Chip)`
  font-weight: ${(props) => props.theme.typography.fontWeightBold};
  height: 20px;
  position: absolute;
  right: 26px;
  top: 12px;
  background: ${(props) => props.theme.sidebar.badge.background};
  z-index: 1;
  span.MuiChip-label,
  span.MuiChip-label:hover {
    font-size: 11px;
    cursor: pointer;
    color: ${(props) => props.theme.sidebar.badge.color};
    padding-left: ${(props) => props.theme.spacing(2)};
    padding-right: ${(props) => props.theme.spacing(2)};
  }
`;

const ExpandLessIcon = styled(ExpandLess)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const ExpandMoreIcon = styled(ExpandMore)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const SidebarNavListItem = styled(
  ({
    className,
    title,
    href,
    depth = 0,
    children,
    icon: Icon,
    badge,
    open: openProp = false,
  }) => {
    const [open, setOpen] = useState(openProp);

    const handleToggle = () => {
      setOpen((state) => !state);
    };

    return (
      <div className={className}>
        {href ? (
          <NavLink
            to={href}
            data-testid="sidebar-nav-link"
            className={({ isActive, isPending }) =>
              isPending ? "pending" : isActive ? "active" : ""
            }
          >
            <Item className="sidebar-link" depth={depth} onClick={handleToggle}>
              <ItemHighlight className="item-highlight" />
              {Icon && <Icon />}
              <Title depth={depth}>
                {title}
                {badge && <Badge label={badge} />}
              </Title>
              {!!children ? (
                open ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )
              ) : null}
            </Item>
          </NavLink>
        ) : (
          <Item depth={depth} onClick={handleToggle}>
            <ItemHighlight className="item-highlight" />
            {Icon && <Icon />}
            <Title depth={depth}>
              {title}
              {badge && <Badge label={badge} />}
            </Title>
            {!!children ? open ? <ExpandLessIcon /> : <ExpandMoreIcon /> : null}
          </Item>
        )}
        {!!children ? open ? <>{children} </> : null : null}
      </div>
    );
  }
)`
  .active .sidebar-link {
    background-color: ${(props) => props.theme.sidebar.active};
    color: ${(props) => props.theme.sidebar.color};

    .item-highlight {
      opacity: 1;
    }
  }
`;

export default SidebarNavListItem;
